/**
 * useChatMessages — Chat state management hook for AI Chat in Activity Bar.
 *
 * Manages chat message state, sends messages via chatService, and provides
 * a preset-to-message converter for chat preset chips.
 *
 * Phase 1: Stateless (no Firestore persistence). History passed in each request.
 */

import { useState, useCallback, useRef, useEffect } from "react";
import { sendChatMessage } from "../utils/chatService";

let chatIdCounter = 0;

// Per-uid persistence to localStorage so chat history survives logout/login.
// Cap keeps storage bounded and prevents unbounded PHI accumulation.
const HISTORY_KEY_PREFIX = "fg.chatHistory.v2:";
const HISTORY_CAP = 50;

const historyKey = (uid, patientId) => (uid ? `${HISTORY_KEY_PREFIX}${uid}:${patientId || "global"}` : null);

function loadHistory(key) {
  if (!key || typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function saveHistory(key, messages) {
  if (!key || typeof window === "undefined") return;
  try {
    const capped = messages.slice(-HISTORY_CAP);
    if (capped.length === 0) {
      window.localStorage.removeItem(key);
    } else {
      window.localStorage.setItem(key, JSON.stringify(capped));
    }
  } catch {
    // quota / serialization errors — non-fatal
  }
}

function createChatMessage(role, content, extra = {}) {
  return {
    id: `chat-${Date.now()}-${++chatIdCounter}`,
    role,
    content,
    timestamp: Date.now(),
    ...extra,
  };
}

// Convert a chat preset to a natural-language message
const PRESET_TO_TEXT = {
  summarize_document: () => "Summarize this entire document",
  summarize_section: (params) => `Summarize the ${(params?.section || "").replace(/_/g, " ")} section`,
  summarize_recent_history: () => "Summarize this patient's recent visit history",
  find_missing_info: () => "What information is missing from this document?",
  platform_help: (params) => {
    const topicPhrases = {
      export: "How do I export a document?",
      share: "How do I share a patient?",
      record: "How do I record a session?",
      upload: "How do I upload a document?",
      navigate: "How do I navigate the platform?",
      settings: "How do I access settings?",
      overview: "What can you help me with?",
    };
    return topicPhrases[params?.topic] || "What can you help me with?";
  },
  trigger_action: (params) => `Run the ${params?.actionId || "action"}`,
  search_patients: (params) => params?.query || "Search my patients",
};

/**
 * @param {Object} options
 * @param {string} options.userId        — Current user uid (history is partitioned per uid)
 * @param {string} options.patientId     — Current patient ID
 * @param {string} options.documentId    — Current document ID
 * @param {Object} options.documentData  — Lightweight document metadata
 * @param {string} options.pageContext   — Current page context (e.g., 'patientDetail', 'documentEditor')
 * @param {string} options.patientName   — Current patient display name
 * @param {boolean} options.enabled      — Feature gate (aiAssistant flag)
 */
export function useChatMessages({ userId, patientId, documentId, documentData, pageContext, patientName, enabled }) {
  const scopeKey = historyKey(userId, patientId);
  const [stored, setStored] = useState(() => ({ key: scopeKey, messages: loadHistory(scopeKey) }));
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const lastMessageRef = useRef(null);
  const scopeRef = useRef(scopeKey);
  const messagesRef = useRef(stored.messages);
  const requestRef = useRef(null);
  if (scopeRef.current !== scopeKey) {
    scopeRef.current = scopeKey;
    messagesRef.current = loadHistory(scopeKey);
  }
  const chatMessages = stored.key === scopeKey ? stored.messages : [];

  useEffect(() => {
    requestRef.current?.controller.abort();
    requestRef.current = null;
    messagesRef.current = loadHistory(scopeKey);
    setStored({ key: scopeKey, messages: messagesRef.current });
    lastMessageRef.current = null;
    setError(null);
    setIsLoading(false);
    return () => { requestRef.current?.controller.abort(); requestRef.current = null; };
  }, [scopeKey]);

  const setChatMessages = useCallback((updater) => {
    if (scopeRef.current !== scopeKey) return;
    const next = typeof updater === 'function' ? updater(messagesRef.current) : updater;
    messagesRef.current = next;
    saveHistory(scopeKey, next);
    setStored({ key: scopeKey, messages: next });
  }, [scopeKey]);

  const sendMessage = useCallback(async (text) => {
    if (!enabled || !scopeKey || !text?.trim() || requestRef.current?.scope === scopeKey) return;
    const trimmed = text.trim();
    const history = messagesRef.current.filter(m => m.role === 'user' || m.role === 'assistant')
      .slice(-10).map(m => ({ role: m.role === 'assistant' ? 'model' : m.role, content: m.content }));
    const request = { scope: scopeKey, controller: new AbortController() };
    requestRef.current = request;
    setError(null);
    setChatMessages(prev => [...prev, createChatMessage('user', trimmed)]);
    lastMessageRef.current = trimmed;
    setIsLoading(true);
    try {
      const result = await sendChatMessage({ message: trimmed, documentId, patientId, documentData,
        pageContext, patientName, history, signal: request.controller.signal });
      if (scopeRef.current !== scopeKey || requestRef.current !== request) return;
      if (result.success) {
        setChatMessages(prev => [...prev, createChatMessage('assistant', result.message, {
          functionCalled: result.functionCalled, searchResults: result.searchResults || null,
          historyCoverage: result.historyCoverage || null,
        })]);
      } else if (!result.cancelled) setError(result.error || 'Something went wrong.');
    } catch (err) {
      if (scopeRef.current === scopeKey && requestRef.current === request && err.name !== 'AbortError') {
        setError('Something went wrong. Please try again.');
      }
    } finally {
      if (scopeRef.current === scopeKey && requestRef.current === request) {
        requestRef.current = null;
        setIsLoading(false);
      }
    }
  }, [enabled, scopeKey, documentId, patientId, documentData, pageContext, patientName, setChatMessages]);

  /**
   * Send a chat preset (from actions.json chatPresets).
   */
  const sendPreset = useCallback((preset) => {
    if (!preset?.chatFunction) return;
    const converter = PRESET_TO_TEXT[preset.chatFunction];
    if (converter) {
      sendMessage(converter(preset.params));
    } else {
      sendMessage(preset.label || "Help");
    }
  }, [sendMessage]);

  /**
   * Retry the last failed message.
   */
  const retry = useCallback(() => {
    if (lastMessageRef.current) {
      // Remove the last user message that failed so sendMessage re-adds it
      setChatMessages((prev) => {
        const last = [...prev];
        // Find and remove the last user message
        for (let i = last.length - 1; i >= 0; i--) {
          if (last[i].role === "user") {
            last.splice(i, 1);
            break;
          }
        }
        return last;
      });
      setError(null);
      sendMessage(lastMessageRef.current);
    }
  }, [sendMessage, setChatMessages]);

  /**
   * Clear all chat messages and error state.
   */
  const clearChat = useCallback(() => {
    requestRef.current?.controller.abort();
    requestRef.current = null;
    setIsLoading(false);
    setChatMessages([]);
    setError(null);
    lastMessageRef.current = null;
  }, [setChatMessages]);

  return {
    chatMessages,
    sendMessage,
    sendPreset,
    isLoading,
    error,
    retry,
    clearChat,
  };
}
